import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import getIsLoyaltyEsimEnabled from 'loyalty/selectors/getIsLoyaltyEsimEnabled'
import { isEsimExcludedCountry } from 'loyalty/lib/esimGeoExclusion'

// Union of all offer types that can be checked for loyalty benefit eligibility.
export type LoyaltyEligibleOffer = App.AnyOffer | App.VillaOffer | App.VillaOfferSummary | App.BundledOffer
export type LoyaltyEligibleOfferOrProperty = LoyaltyEligibleOffer | App.Property.Property

interface OfferWithProperty {
  property?: {
    geoData?: { countryCode?: string }
    address?: { countryCode?: string }
  }
}

function isProperty(value: LoyaltyEligibleOfferOrProperty): value is App.Property.Property {
  return 'marketedLos' in value && 'rooms' in value
}

function getDestinationCountryCode(offerOrProperty: LoyaltyEligibleOfferOrProperty): string | undefined {
  if (isProperty(offerOrProperty)) {
    return offerOrProperty.location.countryCode
  }

  if (!('property' in offerOrProperty)) return undefined
  const { property } = offerOrProperty as OfferWithProperty
  return property?.geoData?.countryCode ?? property?.address?.countryCode
}

function getIsInternationalDestination(state: App.State, destinationCountry: string): boolean {
  const accountRegion = getLoyaltyAccountRegion(state)
  if (!accountRegion) return false
  const excludedCountryCodes = state.loyalty.config?.esimExcludedCountryCodes ?? []
  return destinationCountry.toLowerCase() !== accountRegion.toLowerCase()
    && !isEsimExcludedCountry(destinationCountry, excludedCountryCodes)
}

function isLoyaltyMember(state: App.State): boolean {
  return !!state.loyalty.account.data
}

export function getIsOfferEsimEligible(state: App.State, offerOrProperty: LoyaltyEligibleOfferOrProperty): boolean {
  if (!getIsLoyaltyEsimEnabled(state)) return false
  if (!isLoyaltyMember(state)) return false
  const destinationCountry = getDestinationCountryCode(offerOrProperty)
  if (!destinationCountry) return false
  return getIsInternationalDestination(state, destinationCountry)
}

export function getIsTourVariationEsimEligible(state: App.State, variation: App.Tours.TourV2OfferVariation): boolean {
  if (!getIsLoyaltyEsimEnabled(state)) return false
  if (!isLoyaltyMember(state)) return false
  const countries = variation?.countriesVisited ?? []
  if (!countries.length) return false
  const accountRegion = getLoyaltyAccountRegion(state)
  if (!accountRegion) return false
  const region = accountRegion.toLowerCase()
  const excludedCountryCodes = state.loyalty.config?.esimExcludedCountryCodes ?? []
  // Aligned with esimOrderUtils: eligible when at least one country is
  // international AND not excluded, rather than rejecting the entire tour when
  // any single country is excluded.
  return countries.some((c) =>
    !!c && c.toLowerCase() !== region && !isEsimExcludedCountry(c, excludedCountryCodes),
  )
}
